import { EducationData, UserProgress } from "../types/education";
import { ProgressAnalytics } from "./skillAssessmentService";

export interface SyncConflict {
  field: string;
  localValue: any;
  remoteValue: any;
  resolvedValue: any;
  resolutionStrategy: "merge" | "local-wins" | "remote-wins" | "manual";
}

export interface SyncResult {
  success: boolean;
  conflicts: SyncConflict[];
  lastSyncTime: string;
  syncedFields: string[];
  errors: string[];
}

export interface BackupData {
  version: string;
  timestamp: string;
  educationData: EducationData;
  analytics: ProgressAnalytics;
  metadata: {
    deviceId: string;
    userAgent: string;
    totalBackups: number;
    compressed: boolean;
  };
}

export interface SyncQueueItem {
  id: string;
  type: "create" | "update" | "delete";
  data: any;
  timestamp: string;
  retryCount: number;
  lastError?: string;
}

export interface ProgressTrackingConfig {
  enableCloudSync: boolean;
  syncInterval: number; // in minutes
  maxRetries: number;
  conflictResolution: "merge" | "local-wins" | "remote-wins" | "manual";
  compressionEnabled: boolean;
  maxLocalBackups: number;
  offlineMode: boolean;
}

export class ProgressTrackingService {
  private readonly STORAGE_KEY = "chromis-education-progress";
  private readonly SYNC_QUEUE_KEY = "chromis-sync-queue";
  private readonly BACKUP_KEY_PREFIX = "chromis-backup-";
  private readonly CONFIG_KEY = "chromis-progress-config";

  private config: ProgressTrackingConfig = {
    enableCloudSync: false,
    syncInterval: 15,
    maxRetries: 3,
    conflictResolution: "merge",
    compressionEnabled: true,
    maxLocalBackups: 5,
    offlineMode: false,
  };

  private syncQueue: SyncQueueItem[] = [];
  private isOnline = navigator.onLine;
  private syncTimer: NodeJS.Timeout | null = null;

  constructor() {
    this.loadConfig();
    this.loadSyncQueue();
    this.setupEventListeners();
    this.startAutoSync();
  }

  // Core Progress Management
  async saveProgress(educationData: EducationData): Promise<void> {
    try {
      // Save to local storage immediately
      await this.saveToLocalStorage(educationData);

      // Queue for cloud sync if enabled
      if (this.config.enableCloudSync && !this.config.offlineMode) {
        this.queueSyncOperation("update", educationData);
      }
    } catch (error) {
      console.error("Failed to save progress:", error);
      throw error;
    }
  }

  async loadProgress(): Promise<EducationData> {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error("Failed to load progress from localStorage:", error);
    }

    // Return default data if nothing stored
    return this.getDefaultEducationData();
  }

  // Cloud Sync Operations
  async syncWithCloud(): Promise<SyncResult> {
    if (!this.config.enableCloudSync || this.config.offlineMode) {
      return {
        success: false,
        conflicts: [],
        lastSyncTime: new Date().toISOString(),
        syncedFields: [],
        errors: ["Cloud sync is disabled"],
      };
    }

    if (!this.isOnline) {
      return {
        success: false,
        conflicts: [],
        lastSyncTime: new Date().toISOString(),
        syncedFields: [],
        errors: ["Device is offline"],
      };
    }

    try {
      const localData = await this.loadProgress();
      const remoteData = await this.fetchRemoteProgress();

      if (!remoteData) {
        // No remote data, push local data
        await this.pushToCloud(localData);
        return {
          success: true,
          conflicts: [],
          lastSyncTime: new Date().toISOString(),
          syncedFields: Object.keys(localData),
          errors: [],
        };
      }

      // Merge local and remote data
      const { mergedData, conflicts } = this.mergeProgressData(
        localData,
        remoteData,
      );

      // Save merged data locally
      await this.saveToLocalStorage(mergedData);

      // Push merged data to cloud
      await this.pushToCloud(mergedData);

      // Clear sync queue
      this.clearSyncQueue();

      return {
        success: true,
        conflicts,
        lastSyncTime: new Date().toISOString(),
        syncedFields: Object.keys(mergedData),
        errors: [],
      };
    } catch (error) {
      console.error("Cloud sync failed:", error);
      return {
        success: false,
        conflicts: [],
        lastSyncTime: new Date().toISOString(),
        syncedFields: [],
        errors: [error instanceof Error ? error.message : "Unknown sync error"],
      };
    }
  }

  // Backup and Restore
  async createBackup(includeAnalytics = true): Promise<string> {
    try {
      const educationData = await this.loadProgress();
      const analytics = includeAnalytics
        ? await this.generateAnalytics()
        : null;

      const backupData: BackupData = {
        version: "1.0.0",
        timestamp: new Date().toISOString(),
        educationData,
        analytics: analytics || this.getDefaultAnalytics(),
        metadata: {
          deviceId: this.getDeviceId(),
          userAgent: navigator.userAgent,
          totalBackups: await this.getBackupCount(),
          compressed: this.config.compressionEnabled,
        },
      };

      const backupId = `backup-${Date.now()}`;
      const backupKey = `${this.BACKUP_KEY_PREFIX}${backupId}`;

      const serializedData = JSON.stringify(backupData);
      const finalData = this.config.compressionEnabled
        ? await this.compressData(serializedData)
        : serializedData;

      localStorage.setItem(backupKey, finalData);

      // Clean up old backups
      await this.cleanupOldBackups();

      return backupId;
    } catch (error) {
      console.error("Failed to create backup:", error);
      throw error;
    }
  }

  async restoreBackup(backupId: string): Promise<void> {
    try {
      const backupKey = `${this.BACKUP_KEY_PREFIX}${backupId}`;
      const storedData = localStorage.getItem(backupKey);

      if (!storedData) {
        throw new Error("Backup not found");
      }

      const decompressedData = this.config.compressionEnabled
        ? await this.decompressData(storedData)
        : storedData;
      const backupData: BackupData = JSON.parse(decompressedData);

      // Validate backup data
      this.validateBackupData(backupData);

      // Restore education data
      await this.saveToLocalStorage(backupData.educationData);

      console.log(`Successfully restored backup from ${backupData.timestamp}`);
    } catch (error) {
      console.error("Failed to restore backup:", error);
      throw error;
    }
  }

  async listBackups(): Promise<
    Array<{ id: string; timestamp: string; size: number }>
  > {
    const backups: Array<{ id: string; timestamp: string; size: number }> = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.BACKUP_KEY_PREFIX)) {
        try {
          const data = localStorage.getItem(key);
          if (data) {
            const decompressedData = this.config.compressionEnabled
              ? await this.decompressData(data)
              : data;
            const backupData: BackupData = JSON.parse(decompressedData);
            backups.push({
              id: key.replace(this.BACKUP_KEY_PREFIX, ""),
              timestamp: backupData.timestamp,
              size: data.length,
            });
          }
        } catch (error) {
          console.error(`Failed to parse backup ${key}:`, error);
        }
      }
    }

    return backups.sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
  }

  async deleteBackup(backupId: string): Promise<void> {
    const backupKey = `${this.BACKUP_KEY_PREFIX}${backupId}`;
    localStorage.removeItem(backupKey);
  }

  // Configuration Management
  updateConfig(newConfig: Partial<ProgressTrackingConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.saveConfig();

    // Restart auto sync with new interval
    if (this.syncTimer) {
      clearInterval(this.syncTimer);
    }
    this.startAutoSync();
  }

  getConfig(): ProgressTrackingConfig {
    return { ...this.config };
  }

  // Offline Support
  enableOfflineMode(): void {
    this.config.offlineMode = true;
    this.saveConfig();
  }

  disableOfflineMode(): void {
    this.config.offlineMode = false;
    this.saveConfig();

    // Trigger sync when coming back online
    if (this.isOnline) {
      this.syncWithCloud();
    }
  }

  getSyncQueueStatus(): { pending: number; failed: number; total: number } {
    const pending = this.syncQueue.filter((item) => !item.lastError).length;
    const failed = this.syncQueue.filter((item) => item.lastError).length;

    return {
      pending,
      failed,
      total: this.syncQueue.length,
    };
  }

  // Private Helper Methods
  private async saveToLocalStorage(
    educationData: EducationData,
  ): Promise<void> {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(educationData));
    } catch (error) {
      console.error("Failed to save to localStorage:", error);
      throw error;
    }
  }

  private getDefaultEducationData(): EducationData {
    return {
      progress: {},
      stats: {
        totalLessons: 0,
        completedLessons: 0,
        totalTimeSpent: 0,
        averageQuizScore: 0,
        currentStreak: 0,
        longestStreak: 0,
      },
      achievements: [],
      bookmarks: [],
      notes: [],
      noteTags: [],
      ratings: [],
      surveys: [],
      comments: [],
    };
  }

  private getDefaultAnalytics(): ProgressAnalytics {
    return {
      overview: {
        totalLessons: 0,
        completedLessons: 0,
        totalTopics: 0,
        completedTopics: 0,
        totalPaths: 0,
        completedPaths: 0,
        overallProgress: 0,
        averageQuizScore: 0,
        totalTimeSpent: 0,
        streakDays: 0,
        lastActivityDate: new Date().toISOString(),
      },
      skills: [],
      insights: [],
      trends: {
        weeklyProgress: [],
        skillProgression: [],
      },
      recommendations: {
        nextTopics: [],
        recommendedPaths: [],
        skillFocus: [],
        timeAllocation: {},
      },
    };
  }

  private mergeProgressData(
    local: EducationData,
    remote: EducationData,
  ): { mergedData: EducationData; conflicts: SyncConflict[] } {
    const conflicts: SyncConflict[] = [];
    const merged: EducationData = {
      progress: {},
      stats: { ...local.stats },
      achievements: [...local.achievements],
      bookmarks: [...local.bookmarks],
      notes: [...local.notes],
      noteTags: [...local.noteTags],
      ratings: [...local.ratings],
      surveys: [...local.surveys],
      comments: [...local.comments],
    };

    // Merge progress data
    const allLessonIds = new Set([
      ...Object.keys(local.progress),
      ...Object.keys(remote.progress),
    ]);

    allLessonIds.forEach((lessonId) => {
      const localProgress = local.progress[lessonId];
      const remoteProgress = remote.progress[lessonId];

      if (!localProgress) {
        merged.progress[lessonId] = remoteProgress;
        return;
      }

      if (!remoteProgress) {
        merged.progress[lessonId] = localProgress;
        return;
      }

      // Merge individual lesson progress
      const mergedProgress = this.mergeLessonProgress(
        localProgress,
        remoteProgress,
      );
      merged.progress[lessonId] = mergedProgress.data;

      if (mergedProgress.conflicts.length > 0) {
        conflicts.push(...mergedProgress.conflicts);
      }
    });

    // Merge stats (take max values for most fields)
    merged.stats = {
      totalLessons: Math.max(
        local.stats.totalLessons,
        remote.stats.totalLessons,
      ),
      completedLessons: Math.max(
        local.stats.completedLessons,
        remote.stats.completedLessons,
      ),
      totalTimeSpent: Math.max(
        local.stats.totalTimeSpent,
        remote.stats.totalTimeSpent,
      ),
      averageQuizScore: Math.max(
        local.stats.averageQuizScore,
        remote.stats.averageQuizScore,
      ),
      currentStreak: Math.max(
        local.stats.currentStreak,
        remote.stats.currentStreak,
      ),
      longestStreak: Math.max(
        local.stats.longestStreak,
        remote.stats.longestStreak,
      ),
    };

    // Merge achievements (union)
    merged.achievements = [
      ...new Set([...local.achievements, ...remote.achievements]),
    ];

    // Merge bookmarks (union)
    merged.bookmarks = [...new Set([...local.bookmarks, ...remote.bookmarks])];

    return { mergedData: merged, conflicts };
  }

  private mergeLessonProgress(
    local: UserProgress,
    remote: UserProgress,
  ): { data: UserProgress; conflicts: SyncConflict[] } {
    const conflicts: SyncConflict[] = [];
    const merged: UserProgress = {
      ...local,
      quizScores: [
        ...new Set([...(local.quizScores || []), ...(remote.quizScores || [])]),
      ],
    };

    // Take the most recent timestamp
    if (new Date(remote.lastAccessed) > new Date(local.lastAccessed)) {
      merged.lastAccessed = remote.lastAccessed;
    }

    // Take max progress
    if (remote.progress > local.progress) {
      merged.progress = remote.progress;
    }

    // Take max time spent
    if (remote.timeSpent > local.timeSpent) {
      merged.timeSpent = remote.timeSpent;
    }

    // If completed in either, mark as completed
    merged.completed = local.completed || remote.completed;

    return { data: merged, conflicts };
  }

  private queueSyncOperation(
    type: "create" | "update" | "delete",
    data: any,
  ): void {
    // Only queue if cloud sync is enabled and not in offline mode
    if (!this.config.enableCloudSync || this.config.offlineMode) {
      return;
    }

    const queueItem: SyncQueueItem = {
      id: `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      data,
      timestamp: new Date().toISOString(),
      retryCount: 0,
    };

    this.syncQueue.push(queueItem);
    this.saveSyncQueue();

    // Try to sync immediately if online
    if (this.isOnline) {
      this.processSyncQueue();
    }
  }

  private async processSyncQueue(): Promise<void> {
    if (!this.isOnline || this.config.offlineMode) return;

    const failedItems: SyncQueueItem[] = [];

    for (const item of this.syncQueue) {
      try {
        await this.syncQueueItem(item);
      } catch (error) {
        item.retryCount++;
        item.lastError =
          error instanceof Error ? error.message : "Unknown error";

        if (item.retryCount < this.config.maxRetries) {
          failedItems.push(item);
        }
      }
    }

    this.syncQueue = failedItems;
    this.saveSyncQueue();
  }

  private async syncQueueItem(item: SyncQueueItem): Promise<void> {
    // Mock implementation - would integrate with actual cloud service
    console.log(`Syncing ${item.type} operation:`, item.id);

    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Simulate occasional failures for testing
    if (Math.random() < 0.1) {
      throw new Error("Simulated sync failure");
    }
  }

  private setupEventListeners(): void {
    window.addEventListener("online", () => {
      this.isOnline = true;
      if (!this.config.offlineMode) {
        this.processSyncQueue();
      }
    });

    window.addEventListener("offline", () => {
      this.isOnline = false;
    });
  }

  private startAutoSync(): void {
    if (this.config.enableCloudSync && !this.config.offlineMode) {
      this.syncTimer = setInterval(
        () => {
          if (this.isOnline) {
            this.syncWithCloud();
          }
        },
        this.config.syncInterval * 60 * 1000,
      );
    }
  }

  private loadConfig(): void {
    try {
      const stored = localStorage.getItem(this.CONFIG_KEY);
      if (stored) {
        this.config = { ...this.config, ...JSON.parse(stored) };
      }
    } catch (error) {
      console.error("Failed to load config:", error);
    }
  }

  private saveConfig(): void {
    try {
      localStorage.setItem(this.CONFIG_KEY, JSON.stringify(this.config));
    } catch (error) {
      console.error("Failed to save config:", error);
    }
  }

  private loadSyncQueue(): void {
    try {
      const stored = localStorage.getItem(this.SYNC_QUEUE_KEY);
      if (stored) {
        this.syncQueue = JSON.parse(stored);
      }
    } catch (error) {
      console.error("Failed to load sync queue:", error);
    }
  }

  private saveSyncQueue(): void {
    try {
      localStorage.setItem(this.SYNC_QUEUE_KEY, JSON.stringify(this.syncQueue));
    } catch (error) {
      console.error("Failed to save sync queue:", error);
    }
  }

  private clearSyncQueue(): void {
    this.syncQueue = [];
    this.saveSyncQueue();
  }

  private getDeviceId(): string {
    let deviceId = localStorage.getItem("chromis-device-id");
    if (!deviceId) {
      deviceId = `device-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem("chromis-device-id", deviceId);
    }
    return deviceId;
  }

  private async getBackupCount(): Promise<number> {
    const backups = await this.listBackups();
    return backups.length;
  }

  private async cleanupOldBackups(): Promise<void> {
    const backups = await this.listBackups();

    if (backups.length > this.config.maxLocalBackups) {
      const backupsToDelete = backups.slice(this.config.maxLocalBackups);

      for (const backup of backupsToDelete) {
        await this.deleteBackup(backup.id);
      }
    }
  }

  private validateBackupData(backupData: BackupData): void {
    if (!backupData.educationData) {
      throw new Error("Invalid backup: missing education data");
    }

    if (!backupData.version) {
      throw new Error("Invalid backup: missing version");
    }

    if (!backupData.timestamp) {
      throw new Error("Invalid backup: missing timestamp");
    }
  }

  private async compressData(data: string): Promise<string> {
    // Simple compression simulation - in real implementation would use compression library
    return btoa(data);
  }

  private async decompressData(compressedData: string): Promise<string> {
    // Simple decompression simulation
    try {
      return atob(compressedData);
    } catch (error) {
      throw new Error("Failed to decompress backup data");
    }
  }

  private async fetchRemoteProgress(): Promise<EducationData | null> {
    // Mock implementation - would integrate with actual cloud service
    return null;
  }

  private async pushToCloud(data: EducationData): Promise<void> {
    // Mock implementation - would integrate with actual cloud service
    console.log("Pushing data to cloud:", data);
  }

  private async generateAnalytics(): Promise<ProgressAnalytics> {
    // Mock implementation - would integrate with skill assessment service
    return this.getDefaultAnalytics();
  }
}

// Export singleton instance
export const progressTrackingService = new ProgressTrackingService();

// Test-only method to update online status
export const setTestOnlineStatus = (isOnline: boolean) => {
  (progressTrackingService as any).isOnline = isOnline;
};
